import React from 'react';

const KittyThermalPrint = ({ enrollment, customer, scheme }) => {
  if (!enrollment || !customer || !scheme) return null;

  const fmt = (d) => { if (!d) return 'N/A'; return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); };
  const cur = (a) => (parseFloat(a) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const totalPeriods = enrollment.schedule?.length || 0;
  const paidPeriods = enrollment.schedule?.filter(p => p.status === 'Paid').length || 0;
  const progressPercent = totalPeriods > 0 ? ((paidPeriods / totalPeriods) * 100).toFixed(1) : 0;
  const amountCollected = enrollment.schedule?.reduce((sum, p) => sum + (parseFloat(p.actual_amount_paid) || 0), 0) || 0;
  const gramsAccumulated = enrollment.schedule?.reduce((sum, p) => sum + (parseFloat(p.grams_accumulated) || 0), 0) || 0;

  const bx = { border: '2px solid #000', padding: '2px 3px', marginBottom: '1px' };
  const hd = { fontSize: '14px', fontWeight: '900', borderBottom: '2px solid #000', paddingBottom: '1px', marginBottom: '2px', textTransform: 'uppercase' };
  const r = { fontSize: '13px', fontWeight: '700', marginBottom: '1px', lineHeight: '1.25' };

  return (
    <div id="kitty-thermal-print-content" className="print-content-hidden">
      {enrollment?.has_provisional_changes && <div className="provisional-watermark">PROVISIONAL</div>}
      <div style={{ fontFamily: "'Roboto Mono', 'Courier New', monospace", fontSize: '13px', lineHeight: '1.15', color: '#000', padding: '2mm', width: '74mm', maxWidth: '74mm', margin: '0 auto', fontWeight: '700' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', borderBottom: '2px solid #000', paddingBottom: '2px', marginBottom: '1px' }}>
          <div style={{ fontSize: '16px', fontWeight: '900' }}>KITTY ENROLLMENT</div>
          <div style={{ fontSize: '14px', fontWeight: '900' }}>{enrollment.enrollment_id}</div>
          <div style={r}>{fmt(enrollment.enrollment_date || enrollment.created_at)}</div>
        </div>

        {/* Customer */}
        <div style={bx}>
          <div style={hd}>CUSTOMER</div>
          <div style={{ fontSize: '14px', fontWeight: '900' }}>{customer.name}</div>
          {customer.mobile && (
            <div style={r}>Mob: {customer.mobile}{customer.alternate_mobile ? ` / ${customer.alternate_mobile}` : ''}</div>
          )}
          {customer.email && <div style={{ ...r, wordBreak: 'break-all' }}>Email: {customer.email}</div>}
          {customer.address && <div style={r}>Addr: {customer.address}</div>}
        </div>

        {/* Summary */}
        <div style={bx}>
          <div style={hd}>SUMMARY</div>
          <div style={{ ...r, fontSize: '14px', fontWeight: '900' }}>Scheme: {scheme.scheme_name}</div>
          <div style={r}>Progress: {paidPeriods}/{totalPeriods} ({progressPercent}%)</div>
          <div style={r}>Collected: Rs.{cur(amountCollected)}</div>
          <div style={r}>Grams: {gramsAccumulated.toFixed(3)}g</div>
          {enrollment.maturity_date && (
            <div style={{ ...r, borderTop: '1px dashed #000', paddingTop: '1px', marginTop: '2px', fontWeight: '900' }}>
              Maturity: {fmt(enrollment.maturity_date)}
            </div>
          )}
        </div>

        {/* Payment Schedule */}
        <div style={{ marginBottom: '1px' }}>
          <div style={hd}>PAYMENT SCHEDULE</div>
          {enrollment.schedule && enrollment.schedule.map((period, idx) => {
            const periodPayments = period.payment_details || [];
            const isPaid = period.status === 'Paid';
            return (
              <div key={idx} style={{ ...bx, border: isPaid ? '2px solid #000' : '1px solid #999' }}>
                {/* Period Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '900' }}>#{period.period_number}</div>
                  <div style={{
                    padding: '1px 4px',
                    border: isPaid ? '2px solid #000' : '1px solid #999',
                    fontWeight: '900',
                    fontSize: '12px',
                    backgroundColor: 'transparent',
                    color: isPaid ? '#000' : '#666'
                  }}>
                    {isPaid ? 'PAID' : period.status?.toUpperCase() || 'PENDING'}
                  </div>
                </div>

                <div style={r}>Due: {fmt(period.due_date)} | Amt: Rs.{cur(period.expected_amount || period.due_amount || 0)}</div>

                {/* Payment Details */}
                {isPaid && periodPayments.length > 0 && (
                  <div style={{ marginTop: '2px', paddingTop: '2px', borderTop: '1px dashed #000' }}>
                    {periodPayments.map((payment, pIdx) => (
                      <div key={pIdx} style={{ marginBottom: '2px', paddingLeft: '3px', borderLeft: '3px solid #000' }}>
                        <div style={{ fontSize: '13px', fontWeight: '900', marginBottom: '1px' }}>
                          {payment.payment_type || 'Cash'} Payment
                        </div>
                        {payment.payment_date && <div style={r}>Date: {fmt(payment.payment_date)}</div>}
                        {payment.amount && <div style={{ ...r, fontWeight: '900' }}>Amount: Rs.{cur(payment.amount)}</div>}

                        {/* UPI */}
                        {payment.payment_type === 'UPI' && (<>
                          {payment.upi_id && <div style={r}>UPI: {payment.upi_id}</div>}
                          {payment.reference && <div style={r}>Ref: {payment.reference}</div>}
                        </>)}
                        {/* Card */}
                        {payment.payment_type === 'Card' && (<>
                          {payment.card_bank_name && <div style={r}>Bank: {payment.card_bank_name}</div>}
                          {payment.card_last4 && <div style={r}>Card: **** {payment.card_last4}</div>}
                          {payment.reference && <div style={r}>Ref: {payment.reference}</div>}
                        </>)}
                        {/* Bank Transfer */}
                        {payment.payment_type === 'Bank Transfer' && (<>
                          {payment.bank_subtype && <div style={r}>Type: {payment.bank_subtype.toUpperCase()}</div>}
                          {payment.bank_name && <div style={r}>Bank: {payment.bank_name}</div>}
                          {payment.utr_ref && <div style={r}>UTR: {payment.utr_ref}</div>}
                          {payment.cheque_no && <div style={r}>Cheque: {payment.cheque_no}</div>}
                          {payment.payer_acct_last4 && <div style={r}>Acct: **** {payment.payer_acct_last4}</div>}
                        </>)}
                        {/* Old Gold */}
                        {payment.payment_type === 'Old Gold' && (<>
                          {payment.old_gold_gross_wt && <div style={r}>Gross: {payment.old_gold_gross_wt}g{payment.old_gold_net_wt ? ` | Net: ${payment.old_gold_net_wt}g` : ''}</div>}
                          {payment.old_gold_purity && <div style={r}>Purity: {payment.old_gold_purity}%{payment.old_gold_fine_wt ? ` | Fine: ${payment.old_gold_fine_wt}g` : ''}</div>}
                        </>)}
                        {/* Metal Credit */}
                        {payment.payment_type === 'Metal Credit' && payment.metal_deposits && (<>
                          {(() => { try { return JSON.parse(payment.metal_deposits).map((dep, di) => (
                            <div key={di} style={r}>Wt: {dep.weight}g @ Rs.{cur(dep.rate)}/g</div>
                          )); } catch(e) { return null; } })()}
                        </>)}
                        {payment.note && <div style={{ ...r, fontSize: '11px', fontStyle: 'italic' }}>Note: {payment.note}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', borderTop: '2px solid #000', paddingTop: '2px', fontSize: '12px' }}>
          <div style={{ fontWeight: '900' }}>Thank you!</div>
          <div style={{ color: '#666' }}>{new Date().toLocaleString('en-IN')}</div>
        </div>
      </div>
    </div>
  );
};

export default KittyThermalPrint;
